/*
 * Copyright (c) 2026 Paxia AI Pty Ltd. All rights reserved.
 * This software is proprietary and confidential.
 * No part of this codebase may be copied, modified, published, distributed, sublicensed, or used without prior written permission from Paxia AI Pty Ltd.
 */

import React, { useState, useMemo } from 'react';
import {
  generateId,
  saveCustomStyle,
  DEFAULT_LAYOUT_STYLE,
  SHADOW_PRESETS,
  FONT_PRESETS,
} from '@/theme/customThemes';
import { useTheme } from '@/context/ThemeContext';
import { createPersonalTheme, updatePersonalTheme } from '@/api/themeApi';

/* ════════════════════════════════════════════════════════════
   Layout-style editor — create or edit a custom layout style
   ════════════════════════════════════════════════════════════ */

const EMOJI_OPTIONS = ['🎨', '📐', '🔲', '🟦', '⬛', '💠', '🔷', '🧊', '🌀', '🎯', '✨', '🪄'];

const RADIUS_PRESETS = [
  { id: 'none',     label: 'None (sharp)',    values: { radiusSm: '0', radiusMd: '0', radiusLg: '0', radiusXl: '0', radius2xl: '0' } },
  { id: 'subtle',   label: 'Subtle',          values: { radiusSm: '0.125rem', radiusMd: '0.25rem', radiusLg: '0.375rem', radiusXl: '0.5rem', radius2xl: '0.75rem' } },
  { id: 'standard', label: 'Standard',        values: { radiusSm: '0.25rem', radiusMd: '0.375rem', radiusLg: '0.5rem', radiusXl: '0.75rem', radius2xl: '1rem' } },
  { id: 'rounded',  label: 'Rounded',         values: { radiusSm: '0.375rem', radiusMd: '0.5rem', radiusLg: '0.75rem', radiusXl: '1rem', radius2xl: '1.5rem' } },
  { id: 'pill',     label: 'Pill / Max',      values: { radiusSm: '0.5rem', radiusMd: '0.75rem', radiusLg: '1rem', radiusXl: '1.5rem', radius2xl: '9999px' } },
];

const LETTER_SPACING_OPTIONS = [
  { id: 'tighter', label: 'Tighter', value: '-0.05em' },
  { id: 'tight',   label: 'Tight',   value: '-0.025em' },
  { id: 'normal',  label: 'Normal',  value: 'normal' },
  { id: 'wide',    label: 'Wide',    value: '0.025em' },
  { id: 'wider',   label: 'Wider',   value: '0.05em' },
];

const TRANSFORM_OPTIONS = [
  { id: 'none',       label: 'None',       value: 'none' },
  { id: 'uppercase',  label: 'UPPERCASE',  value: 'uppercase' },
  { id: 'capitalize', label: 'Capitalize', value: 'capitalize' },
];

const CARD_BORDER_OPTIONS = [
  { id: '0px', label: 'None',   value: '0px' },
  { id: '1px', label: '1px',    value: '1px' },
  { id: '2px', label: '2px',    value: '2px' },
];

const CARD_PADDING_OPTIONS = [
  { id: '1rem',   label: 'Compact (1rem)' },
  { id: '1.25rem', label: 'Default (1.25rem)' },
  { id: '1.5rem', label: 'Spacious (1.5rem)' },
  { id: '2rem',   label: 'Roomy (2rem)' },
];

export default function LayoutStyleEditor({ existing, onClose }) {
  const { refreshCustomThemes, setStyle } = useTheme();
  const isEditing = !!existing?.id;

  const [label, setLabel]       = useState(existing?.label || '');
  const [icon, setIcon]         = useState(existing?.icon || '🎨');
  const [description, setDesc]  = useState(existing?.description || '');
  const [vars, setVars]         = useState(() => {
    if (existing?.vars) return { ...DEFAULT_LAYOUT_STYLE, ...existing.vars };
    return { ...DEFAULT_LAYOUT_STYLE };
  });

  const update = (key, val) => setVars((prev) => ({ ...prev, [key]: val }));
  const applyRadiusPreset = (preset) => setVars((prev) => ({ ...prev, ...preset.values }));

  const currentRadiusPreset = useMemo(
    () => RADIUS_PRESETS.find((p) =>
      Object.entries(p.values).every(([k, v]) => vars[k] === v),
    )?.id || 'custom',
    [vars],
  );

  const handleSave = () => {
    if (!label.trim()) return;
    const finalId = existing?.id || generateId(label);
    // Save to localStorage (instant)
    saveCustomStyle({ id: finalId, label: label.trim(), icon, description, vars });
    // Persist to backend (best-effort)
    const payload = {
      theme_id: finalId,
      theme_type: 'style',
      label: label.trim(),
      icon,
      description,
      data: vars,
    };
    const token = localStorage.getItem('token');
    if (token) {
      (isEditing ? updatePersonalTheme(finalId, payload) : createPersonalTheme(payload))
        .catch(() => { /* localStorage is the fallback */ });
    }
    refreshCustomThemes();
    setStyle(finalId);
    onClose();
  };

  /* ── Mini live preview ── */
  const shadowVal = SHADOW_PRESETS[vars.shadowPreset] || SHADOW_PRESETS.subtle;
  const fontVal = FONT_PRESETS.find((f) => f.id === vars.fontPrimary)?.value || 'Arial, sans-serif';

  const Preview = () => (
    <div className="p-4 rounded-lg border border-borderDefault bg-backgroundInput">
      <div className="flex gap-3">
        {/* Card preview */}
        <div
          className="flex-1 p-3 bg-backgroundSurface text-textPrimary"
          style={{
            borderRadius: vars.radiusLg,
            boxShadow: shadowVal.md,
            fontFamily: fontVal,
            letterSpacing: vars.letterSpacing,
            border: `${vars.cardBorderWidth} solid var(--color-border-default)`,
            padding: vars.cardPadding,
          }}
        >
          <div className="text-sm font-semibold mb-1">Card Preview</div>
          <div className="text-xs text-textSecondary mb-2">This shows how cards will look.</div>
          <div className="flex gap-2">
            <button
              className="px-3 py-1 bg-primary text-textAccent text-xs font-medium"
              style={{
                borderRadius: vars.radiusMd,
                textTransform: vars.textTransformBtn,
                letterSpacing: vars.letterSpacing,
              }}
            >
              Button
            </button>
            <input
              readOnly
              placeholder="Input field"
              className="px-2 py-1 text-xs bg-backgroundInput border border-borderDefault text-textSecondary"
              style={{ borderRadius: vars.radiusSm }}
            />
          </div>
        </div>
        {/* Badge / pill preview */}
        <div className="flex flex-col gap-2 items-end">
          <span
            className="px-2 py-0.5 text-[10px] bg-primary/10 text-primary"
            style={{ borderRadius: vars.radius2xl }}
          >
            Badge
          </span>
          <span
            className="px-2 py-0.5 text-[10px] bg-success/10 text-success"
            style={{ borderRadius: vars.radiusMd }}
          >
            Status
          </span>
          <div
            className="w-14 h-14 bg-backgroundInput border border-borderDefault"
            style={{ borderRadius: vars.radiusXl, boxShadow: shadowVal.sm }}
          />
        </div>
      </div>
    </div>
  );

  return (
    <div className="bg-backgroundSurface rounded-lg border border-borderDefault p-6 space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <h3 className="text-lg font-semibold text-textPrimary">
          {isEditing ? 'Edit Layout Style' : 'Create Layout Style'}
        </h3>
        <button onClick={onClose} className="text-textSecondary hover:text-textPrimary text-xl leading-none">&times;</button>
      </div>

      {/* Meta */}
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
        <div>
          <label className="block text-xs font-medium text-textSecondary mb-1">Name</label>
          <input
            type="text"
            value={label}
            onChange={(e) => setLabel(e.target.value)}
            placeholder="My Custom Layout"
            className="w-full px-3 py-2 rounded-md bg-backgroundInput border border-borderDefault text-textPrimary text-sm focus:outline-none focus:ring-2 focus:ring-primary"
          />
        </div>
        <div>
          <label className="block text-xs font-medium text-textSecondary mb-1">Icon</label>
          <div className="flex flex-wrap gap-1">
            {EMOJI_OPTIONS.map((e) => (
              <button
                key={e}
                onClick={() => setIcon(e)}
                className={`w-8 h-8 rounded flex items-center justify-center text-base border transition-colors ${
                  icon === e ? 'border-primary bg-primary/10' : 'border-transparent hover:bg-backgroundInput'
                }`}
              >
                {e}
              </button>
            ))}
          </div>
        </div>
        <div>
          <label className="block text-xs font-medium text-textSecondary mb-1">Description</label>
          <input
            type="text"
            value={description}
            onChange={(e) => setDesc(e.target.value)}
            placeholder="Brief style description"
            className="w-full px-3 py-2 rounded-md bg-backgroundInput border border-borderDefault text-textPrimary text-sm focus:outline-none focus:ring-2 focus:ring-primary"
          />
        </div>
      </div>

      {/* Live preview */}
      <div>
        <label className="block text-xs font-medium text-textSecondary mb-2">Live Preview</label>
        <Preview />
      </div>

      {/* ── Border Radius ── */}
      <div>
        <h4 className="text-xs font-semibold text-textSecondary uppercase tracking-wider mb-2">Border Radius</h4>
        <div className="flex flex-wrap gap-2 mb-3">
          {RADIUS_PRESETS.map((p) => (
            <button
              key={p.id}
              onClick={() => applyRadiusPreset(p)}
              className={`px-3 py-1.5 rounded text-xs border transition-colors ${
                currentRadiusPreset === p.id
                  ? 'border-primary bg-primary/10 text-primary'
                  : 'border-borderDefault text-textSecondary hover:bg-backgroundInput'
              }`}
            >
              {p.label}
            </button>
          ))}
          {currentRadiusPreset === 'custom' && (
            <span className="px-3 py-1.5 rounded text-xs border border-accent/50 bg-accent/10 text-accent">Custom</span>
          )}
        </div>
        <div className="grid grid-cols-5 gap-2">
          {['radiusSm', 'radiusMd', 'radiusLg', 'radiusXl', 'radius2xl'].map((key) => (
            <div key={key}>
              <label className="text-[10px] text-textSecondary">{key.replace('radius', '').toUpperCase()}</label>
              <input
                type="text"
                value={vars[key]}
                onChange={(e) => update(key, e.target.value)}
                className="w-full px-2 py-1 text-xs bg-backgroundInput border border-borderDefault rounded text-textPrimary font-mono focus:outline-none focus:border-primary"
              />
            </div>
          ))}
        </div>
      </div>

      {/* ── Shadows ── */}
      <div>
        <h4 className="text-xs font-semibold text-textSecondary uppercase tracking-wider mb-2">Box Shadows</h4>
        <div className="flex flex-wrap gap-2">
          {Object.entries(SHADOW_PRESETS).map(([id, preset]) => (
            <button
              key={id}
              onClick={() => update('shadowPreset', id)}
              className={`px-3 py-1.5 rounded text-xs border transition-colors ${
                vars.shadowPreset === id
                  ? 'border-primary bg-primary/10 text-primary'
                  : 'border-borderDefault text-textSecondary hover:bg-backgroundInput'
              }`}
            >
              {preset.label}
            </button>
          ))}
        </div>
      </div>

      {/* ── Font ── */}
      <div>
        <h4 className="text-xs font-semibold text-textSecondary uppercase tracking-wider mb-2">Font Family</h4>
        <div className="flex flex-wrap gap-2">
          {FONT_PRESETS.map((f) => (
            <button
              key={f.id}
              onClick={() => update('fontPrimary', f.id)}
              className={`px-3 py-1.5 rounded text-xs border transition-colors ${
                vars.fontPrimary === f.id
                  ? 'border-primary bg-primary/10 text-primary'
                  : 'border-borderDefault text-textSecondary hover:bg-backgroundInput'
              }`}
              style={{ fontFamily: f.value }}
            >
              {f.label}
            </button>
          ))}
        </div>
      </div>

      {/* ── Typography ── */}
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
        <div>
          <h4 className="text-xs font-semibold text-textSecondary uppercase tracking-wider mb-2">Letter Spacing</h4>
          <div className="flex flex-wrap gap-1">
            {LETTER_SPACING_OPTIONS.map((o) => (
              <button
                key={o.id}
                onClick={() => update('letterSpacing', o.value)}
                className={`px-2 py-1 rounded text-xs border transition-colors ${
                  vars.letterSpacing === o.value
                    ? 'border-primary bg-primary/10 text-primary'
                    : 'border-borderDefault text-textSecondary hover:bg-backgroundInput'
                }`}
              >
                {o.label}
              </button>
            ))}
          </div>
        </div>
        <div>
          <h4 className="text-xs font-semibold text-textSecondary uppercase tracking-wider mb-2">Button Text Transform</h4>
          <div className="flex flex-wrap gap-1">
            {TRANSFORM_OPTIONS.map((o) => (
              <button
                key={o.id}
                onClick={() => update('textTransformBtn', o.value)}
                className={`px-2 py-1 rounded text-xs border transition-colors ${
                  vars.textTransformBtn === o.value
                    ? 'border-primary bg-primary/10 text-primary'
                    : 'border-borderDefault text-textSecondary hover:bg-backgroundInput'
                }`}
              >
                {o.label}
              </button>
            ))}
          </div>
        </div>
      </div>

      {/* ── Card Appearance ── */}
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        <div>
          <h4 className="text-xs font-semibold text-textSecondary uppercase tracking-wider mb-2">Card Border Width</h4>
          <div className="flex gap-2">
            {CARD_BORDER_OPTIONS.map((o) => (
              <button
                key={o.id}
                onClick={() => update('cardBorderWidth', o.value)}
                className={`px-3 py-1.5 rounded text-xs border transition-colors ${
                  vars.cardBorderWidth === o.value
                    ? 'border-primary bg-primary/10 text-primary'
                    : 'border-borderDefault text-textSecondary hover:bg-backgroundInput'
                }`}
              >
                {o.label}
              </button>
            ))}
          </div>
        </div>
        <div>
          <h4 className="text-xs font-semibold text-textSecondary uppercase tracking-wider mb-2">Card Padding</h4>
          <div className="flex flex-wrap gap-2">
            {CARD_PADDING_OPTIONS.map((o) => (
              <button
                key={o.id}
                onClick={() => update('cardPadding', o.id)}
                className={`px-3 py-1.5 rounded text-xs border transition-colors ${
                  vars.cardPadding === o.id
                    ? 'border-primary bg-primary/10 text-primary'
                    : 'border-borderDefault text-textSecondary hover:bg-backgroundInput'
                }`}
              >
                {o.label}
              </button>
            ))}
          </div>
        </div>
      </div>

      {/* Actions */}
      <div className="flex justify-end gap-3 pt-2 border-t border-borderDefault">
        <button
          onClick={onClose}
          className="px-4 py-2 rounded-md text-sm border border-borderDefault text-textSecondary hover:bg-backgroundInput transition-colors"
        >
          Cancel
        </button>
        <button
          onClick={handleSave}
          disabled={!label.trim()}
          className="px-4 py-2 rounded-md text-sm bg-primary text-textAccent hover:bg-primaryHover transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
        >
          {isEditing ? 'Save Changes' : 'Create Layout Style'}
        </button>
      </div>
    </div>
  );
}
